import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { LETTER_PATTERNS, getLetterCells } from '../lib/letterPatterns';
import { CATEGORY_MAP } from '../lib/categories';

/**
 * MultiColorCell: renders a single day cell that may contain multiple issues.
 * issues: array of Issue documents on that day/category
 */
const MultiColorCell = ({ issues, legendMap, size, isToday, onClick, category, day }) => {
  const openIssues = issues.filter((i) => !i.is_closed);
  const closedIssues = issues.filter((i) => i.is_closed);
  const isAllClosed = issues.length > 0 && openIssues.length === 0;
  const hasRepeat = issues.some((i) => i.repeat_issue);
  const hasTemp = issues.some((i) => (i.temporary_cm || '').length > 0);
  const hasPerm = issues.some((i) => (i.permanent_cm || '').length > 0);

  // Extract unique colors (preserve order by created_at implicitly - already sorted from API)
  const colors = [];
  const seen = new Set();
  issues.forEach((i) => {
    const key = i.color || 'grey';
    if (!seen.has(key)) {
      seen.add(key);
      colors.push(key);
    }
  });

  if (colors.length === 0) colors.push('grey');

  const status0 = legendMap[colors[0]] || legendMap.grey || { bg: '#f1f5f9', ring: '#94a3b8' };

  // Multi-color background
  const backgroundStyle = () => {
    if (colors.length === 1) {
      return { background: status0.bg };
    }
    if (colors.length === 2) {
      const c1 = legendMap[colors[0]]?.bg || '#e5e7eb';
      const c2 = legendMap[colors[1]]?.bg || '#e5e7eb';
      return {
        background: `linear-gradient(135deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`,
      };
    }
    if (colors.length === 3) {
      const c1 = legendMap[colors[0]]?.bg || '#e5e7eb';
      const c2 = legendMap[colors[1]]?.bg || '#e5e7eb';
      const c3 = legendMap[colors[2]]?.bg || '#e5e7eb';
      return {
        background: `linear-gradient(135deg, ${c1} 0%, ${c1} 33%, ${c2} 33%, ${c2} 66%, ${c3} 66%, ${c3} 100%)`,
      };
    }
    // 4+ colors: 2x2 grid
    const c = colors.slice(0, 4).map((k) => legendMap[k]?.bg || '#e5e7eb');
    return {
      background: `conic-gradient(from 0deg, ${c[0]} 0deg 90deg, ${c[1]} 90deg 180deg, ${c[2]} 180deg 270deg, ${c[3]} 270deg 360deg)`,
    };
  };

  // Border color: use the "worst" color's ring (red > yellow > green)
  const priority = { red: 3, orange: 3, yellow: 2, green: 1, grey: 0 };
  const worst = colors.reduce((a, b) => ((priority[b] || 0) > (priority[a] || 0) ? b : a), colors[0]);
  const borderColor = legendMap[worst]?.ring || '#94a3b8';

  return (
    <button
      onClick={onClick}
      title={`${category.label} · Day ${day.getDate()}${
        issues.length > 0 ? ` · ${issues.length} issue${issues.length > 1 ? 's' : ''}` : ''
      }${hasRepeat ? ' · Repeat' : ''}${isAllClosed ? ' · All closed' : ''}`}
      className={`relative flex items-center justify-center rounded-[4px] transition-all hover:scale-110 hover:shadow-md focus:outline-none focus:z-10 ${
        isToday ? 'ring-2 ring-slate-900 ring-offset-1' : ''
      }`}
      style={{
        width: size,
        height: size,
        border: `2px solid ${borderColor}`,
        opacity: isAllClosed ? 0.6 : 1,
        ...backgroundStyle(),
      }}
    >
      <span
        className="text-[9px] font-bold leading-none"
        style={{
          color: colors.length === 1 && colors[0] === 'grey' ? '#64748b' : '#0f172a',
          textDecoration: isAllClosed ? 'line-through' : 'none',
          textShadow: colors.length > 1 ? '0 0 2px rgba(255,255,255,0.9)' : 'none',
        }}
      >
        {day.getDate()}
      </span>
      {/* Issue count badge for multiple */}
      {issues.length > 1 && (
        <div
          className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-[3px] rounded-full bg-slate-900 border-2 border-white flex items-center justify-center shadow-sm"
          title={`${issues.length} issues`}
        >
          <span className="text-[8px] font-bold text-white leading-none">
            {issues.length}
          </span>
        </div>
      )}
      {/* Repeat indicator */}
      {hasRepeat && (
        <div
          className="absolute -top-1.5 -left-1.5 w-3.5 h-3.5 rounded-full bg-amber-500 border-2 border-white flex items-center justify-center shadow-sm animate-pulse"
          title="Repeat Issue"
        >
          <AlertTriangle className="h-1.5 w-1.5 text-white" strokeWidth={3} />
        </div>
      )}
      {/* Closed indicator */}
      {isAllClosed && (
        <div
          className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white flex items-center justify-center shadow-sm translate-x-1 translate-y-1"
          title="All closed"
        >
          <CheckCircle2 className="h-1.5 w-1.5 text-white" strokeWidth={3} />
        </div>
      )}
      {/* CM indicators */}
      {(hasTemp || hasPerm) && !isAllClosed && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
          {hasTemp && (
            <div
              className="w-2 h-2 rounded-full bg-orange-500 border border-white shadow-sm"
              title="Temporary CM applied"
            />
          )}
          {hasPerm && (
            <div
              className="w-2 h-2 rounded-full bg-blue-600 border border-white shadow-sm"
              title="Permanent CM applied"
            />
          )}
        </div>
      )}
    </button>
  );
};

const LetterMosaic = ({
  letterKey,
  days,
  issuesByDate,
  legendMap,
  onDayClick,
  todayIso,
  cellSize = 28,
  gap = 4,
}) => {
  const pattern = LETTER_PATTERNS[letterKey];
  const cells = getLetterCells(letterKey);
  const rows = pattern.length;
  const cols = pattern[0].length;
  const category = CATEGORY_MAP[letterKey];

  const cellDayMap = new Map();
  cells.forEach((cell, idx) => {
    if (idx < days.length) cellDayMap.set(`${cell.row},${cell.col}`, idx);
  });

  const gridWidth = cols * cellSize + (cols - 1) * gap;
  const gridHeight = rows * cellSize + (rows - 1) * gap;

  return (
    <div className="flex flex-col items-center">
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
          gap: `${gap}px`,
          width: gridWidth,
          height: gridHeight,
        }}
      >
        {pattern.map((row, r) =>
          row.split('').map((cellChar, c) => {
            const isActive = cellChar === '1';
            const dayIdx = cellDayMap.get(`${r},${c}`);
            const day = dayIdx !== undefined ? days[dayIdx] : null;
            let iso = null;
            let isToday = false;
            if (day) {
              iso =
                day.getFullYear() +
                '-' +
                String(day.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(day.getDate()).padStart(2, '0');
              isToday = iso === todayIso;
            }

            if (!isActive) {
              return (
                <div
                  key={`${r}-${c}`}
                  style={{ width: cellSize, height: cellSize, background: 'transparent' }}
                />
              );
            }
            if (!day) {
              return (
                <div
                  key={`${r}-${c}`}
                  style={{
                    width: cellSize,
                    height: cellSize,
                    background: '#f8fafc',
                    border: '1px dashed #e2e8f0',
                    borderRadius: 4,
                  }}
                />
              );
            }

            const issues = issuesByDate[iso] || [];
            return (
              <MultiColorCell
                key={`${r}-${c}`}
                issues={issues}
                legendMap={legendMap}
                size={cellSize}
                isToday={isToday}
                onClick={() => onDayClick(dayIdx)}
                category={category}
                day={day}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default LetterMosaic;
